import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Patient, Room, PatientStatus, STATUS_CONFIG, SurgerySubStatus, SurgeryCondition, SUB_STATUS_CONFIG } from '../types';
import * as storageService from '../services/storageService';
import * as cloudflareService from '../services/cloudflareService';
import { ChevronLeft, User, Clock, Scissors, Activity, FileText, RefreshCw, ClipboardList, DoorOpen, Syringe, HeartPulse, LogOut, CheckCircle2, X, Bed, MessageSquare, Users, BellRing, BellOff, Sun, Moon } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { usePatientSync } from '../hooks/usePatientSync';
import { useRoomsListSync } from '../hooks/useRoomsListSync';
import { useHospitalSync } from '../hooks/useHospitalSync';
import { AdminNavbarUser } from '../components/AdminNavbarUser';
import BugReportWidget from '../components/BugReportWidget';

const StaffRoomControl: React.FC = () => {
  const { roomId, patientId } = useParams<{ roomId?: string, patientId?: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const authData = storageService.getAuthData();
  
  const { data: hospital } = useHospitalSync(authData?.hospitalId);
  const { data: rooms = [] } = useRoomsListSync(authData?.hospitalId);
  
  const derivedPatientId = patientId || (roomId ? rooms.find(r => r.id === roomId)?.currentPatientId : null) || null;
  const { data: patient } = usePatientSync(derivedPatientId);

  const branding = hospital?.branding || {
    primaryColor: '#5B65DC',
    secondaryColor: '#122056',
    logoUrl: '',
    logoDarkUrl: '',
    backgroundColor: '#f2f2f7'
  };

  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);

  const [isDarkMode, setIsDarkMode] = useState(() => {
     if (!authData) return true;
     const saved = localStorage.getItem(`staff_dark_mode_${authData.hospitalId}`);
     if (saved !== null) return saved === 'true';
     return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  
  const [timeToNextReminder, setTimeToNextReminder] = useState<string | null>(null);
  const storageKeyId = roomId || patientId || 'default';

  const [isUpdating, setIsUpdating] = useState(false);
  const [isSoundEnabled, setIsSoundEnabled] = useState(
    localStorage.getItem(`sound_enabled_${storageKeyId}`) !== 'false'
  );
  const [selectedSound, setSelectedSound] = useState(
    localStorage.getItem(`sound_type_${storageKeyId}`) || 'campana'
  );
  const lastChimeRef = React.useRef<number>(0);

  const playReminderChime = (soundType: string = selectedSound) => {
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();

      if (soundType === 'campana') {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(523.25, ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(659.25, ctx.currentTime + 0.1);
        osc.frequency.setValueAtTime(659.25, ctx.currentTime + 0.3);
        osc.frequency.exponentialRampToValueAtTime(783.99, ctx.currentTime + 0.4);

        gain.gain.setValueAtTime(0, ctx.currentTime);
        gain.gain.linearRampToValueAtTime(0.4, ctx.currentTime + 0.05);
        gain.gain.exponentialRampToValueAtTime(0.1, ctx.currentTime + 0.3);
        gain.gain.linearRampToValueAtTime(0.4, ctx.currentTime + 0.35);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 1.5);

        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 1.5);
      }
      else if (soundType === 'alerta') {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'square';
        osc.frequency.setValueAtTime(800, ctx.currentTime);

        gain.gain.setValueAtTime(0, ctx.currentTime);
        for (let i = 0; i < 3; i++) {
          gain.gain.setValueAtTime(0.1, ctx.currentTime + i * 0.2);
          gain.gain.setValueAtTime(0, ctx.currentTime + i * 0.2 + 0.1);
        }
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.6);
      }
      else if (soundType === 'clasico') {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';

        gain.gain.setValueAtTime(0.3, ctx.currentTime);
        osc.frequency.setValueAtTime(440, ctx.currentTime);
        osc.frequency.setValueAtTime(480, ctx.currentTime + 0.2);
        osc.frequency.setValueAtTime(440, ctx.currentTime + 0.4);
        osc.frequency.setValueAtTime(480, ctx.currentTime + 0.6);

        gain.gain.setValueAtTime(0, ctx.currentTime + 0.8);

        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.8);
      }
      else if (soundType === 'pedo') {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        const filter = ctx.createBiquadFilter();

        osc.connect(filter);
        filter.connect(gain);
        gain.connect(ctx.destination);

        osc.type = 'sawtooth';

        osc.frequency.setValueAtTime(80, ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(30, ctx.currentTime + 0.6);

        filter.type = 'lowpass';
        filter.frequency.setValueAtTime(500, ctx.currentTime);
        filter.frequency.exponentialRampToValueAtTime(100, ctx.currentTime + 0.6);

        gain.gain.setValueAtTime(0, ctx.currentTime);
        gain.gain.linearRampToValueAtTime(0.8, ctx.currentTime + 0.05);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.6);

        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.6);
      }

    } catch (e) { console.error("Audio error", e) }
  };

  useEffect(() => {
    if (!isSoundEnabled) {
      setTimeToNextReminder(null);
      return;
    }

    const intervalId = setInterval(() => {
      let startTimeStr = localStorage.getItem(`reminder_start_${storageKeyId}`);
      if (!startTimeStr) {
        startTimeStr = String(Date.now());
        localStorage.setItem(`reminder_start_${storageKeyId}`, startTimeStr);
      }

      const startTime = parseInt(startTimeStr, 10);
      const diffTotalSeconds = Math.floor((Date.now() - startTime) / 1000);

      const secondsIn15Min = 15 * 60;
      const secondsSinceLastChime = diffTotalSeconds % secondsIn15Min;
      const remainingSeconds = secondsIn15Min - secondsSinceLastChime;

      const m = Math.floor(remainingSeconds / 60);
      const s = remainingSeconds % 60;
      setTimeToNextReminder(`${m}:${s.toString().padStart(2, '0')}`);

      const diffMinutes = Math.floor(diffTotalSeconds / 60);
      // Trigger chime precisely when reaching a new 15m block
      if (diffMinutes > 0 && diffTotalSeconds % secondsIn15Min === 0 && lastChimeRef.current !== diffMinutes) {
        lastChimeRef.current = diffMinutes;
        playReminderChime(selectedSound);
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [isSoundEnabled, storageKeyId, selectedSound]);

  const handleStatusUpdate = async (status: PatientStatus, subStatus?: SurgerySubStatus, condition?: SurgeryCondition) => {
    if (!patient || isUpdating) return;
    setIsUpdating(true);
    
    // Opt-out of subStatus if it's not SURGERY
    let finalSubStatus = subStatus;
    let finalCondition = condition;
    
    if (status !== PatientStatus.SURGERY) {
      finalSubStatus = undefined;
      finalCondition = SurgeryCondition.NORMAL;
    } else if (!finalSubStatus) {
      finalSubStatus = SurgerySubStatus.INCISION;
      finalCondition = SurgeryCondition.NORMAL;
    }

    try {
      console.log(`[StaffRoomControl] Iniciando update directo a DB para estado: ${status}`);
      
      const updates = { 
        currentStatus: status, 
        currentSubStatus: finalSubStatus, 
        currentCondition: finalCondition,
        updatedAt: patient.updatedAt,
        logs: [
          ...(patient.logs || []),
          {
            status,
            subStatus: finalSubStatus || undefined,
            condition: finalCondition || undefined,
            timestamp: new Date().toISOString(),
            updatedBy: storageService.getAuditName()
          }
        ]
      };

      // 1. Optimistic Update
      queryClient.setQueryData(['patient', patient.id], (old: any) => {
        if (!old) return old;
        return { ...old, ...updates };
      });

      // 2. Perform Backend Request
      await cloudflareService.dbUpdatePatient(patient.id, updates);
      
      // 3. Trigger global sync to keep lists fresh
      window.dispatchEvent(new Event('patients-sync-required'));

      if (status === PatientStatus.EXIT || status === PatientStatus.RECOVERY) {
        console.log(`[StaffRoomControl] Update terminado con éxito. Redirigiendo...`);
        navigate('/staff/dashboard');
      } else {
        setIsUpdating(false);
      }
    } catch (err: any) {
      console.error("[StaffRoomControl] Error crítico al actualizar:", err);
      // Fallback
      queryClient.invalidateQueries({ queryKey: ['patient', patient.id] });
      alert(`ERROR EN SISTEMA: No se pudo actualizar.\n\nDetalle técnico:\n${err.message}`);
      setIsUpdating(false);
    }
  };

  const handleProgressIncrement = async () => {
    if (!patient || isUpdating || patient.surgeryProgress >= 90) return;
    setIsUpdating(true);
    try {
      const newProgress = Math.min(patient.surgeryProgress + 5, 90);
      
      // Optimistic Update
      queryClient.setQueryData(['patient', patient.id], (old: any) => {
        if (!old) return old;
        return { ...old, surgeryProgress: newProgress };
      });

      // DB Request
      await cloudflareService.dbUpdatePatient(patient.id, { surgeryProgress: newProgress, updatedAt: patient.updatedAt });
      window.dispatchEvent(new Event('patients-sync-required'));
    } catch (err) {
      console.error("Error updating progress:", err);
      queryClient.invalidateQueries({ queryKey: ['patient', patient.id] });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleTransfer = async (newRoomId: string) => {
    if (!patient || isUpdating) return;
    setIsUpdating(true);
    try {
      // Optimistic Update locally
      queryClient.setQueryData(['patient', patient.id], (old: any) => {
        if (!old) return old;
        return { ...old, room: newRoomId };
      });
      queryClient.setQueryData(['rooms', authData?.hospitalId], (oldRooms: Room[]) => {
        if (!oldRooms) return oldRooms;
        return oldRooms.map((r: Room) => {
            if (r.id === roomId) return { ...r, currentPatientId: null };
            if (r.id === newRoomId) return { ...r, currentPatientId: patient.id };
            return r;
        });
      });

      // DB Request
      await cloudflareService.dbUpdatePatient(patient.id, { room: newRoomId, updatedAt: patient.updatedAt });
      window.dispatchEvent(new Event('rooms-sync-required'));
      window.dispatchEvent(new Event('patients-sync-required'));
      
      setIsTransferModalOpen(false);
      setIsUpdating(false);
      navigate(`/staff/room/${newRoomId}`);
    } catch (err) {
      console.error("Error transferring room:", err);
      queryClient.invalidateQueries({ queryKey: ['patient', patient.id] });
      queryClient.invalidateQueries({ queryKey: ['rooms', authData?.hospitalId] });
      alert('Error en sistema: No se pudo trasladar de quirófano.');
      setIsUpdating(false);
    }
  };

  const handleDoctorInfoToggle = async () => {
    if (!patient || isUpdating) return;
    setIsUpdating(true);
    try {
      const newStatus = !patient.isDoctorEnCamino;
      
      // Optimistic Update
      queryClient.setQueryData(['patient', patient.id], (old: any) => {
        if (!old) return old;
        return { ...old, isDoctorEnCamino: newStatus };
      });

      // DB Request
      await cloudflareService.dbUpdatePatient(patient.id, { isDoctorEnCamino: newStatus, updatedAt: patient.updatedAt });
      window.dispatchEvent(new Event('patients-sync-required'));
    } catch (err) {
      console.error("Error toggling doctor info:", err);
      queryClient.invalidateQueries({ queryKey: ['patient', patient.id] });
    } finally {
      setIsUpdating(false);
    }
  };

  if (!patient) return <div className="p-8">Cargando datos del paciente...</div>;

  const buttons = [
    { status: PatientStatus.WAITING, ...STATUS_CONFIG[PatientStatus.WAITING], icon: User },
    { status: PatientStatus.PREPARATION, ...STATUS_CONFIG[PatientStatus.PREPARATION], icon: ClipboardList },
    { status: PatientStatus.ROOM_ENTRY, ...STATUS_CONFIG[PatientStatus.ROOM_ENTRY], icon: DoorOpen },
    { status: PatientStatus.ANESTHESIA, ...STATUS_CONFIG[PatientStatus.ANESTHESIA], icon: Syringe },
    { status: PatientStatus.SURGERY, ...STATUS_CONFIG[PatientStatus.SURGERY], icon: Activity },
    { status: PatientStatus.RECOVERY, ...STATUS_CONFIG[PatientStatus.RECOVERY], icon: HeartPulse },
    { status: PatientStatus.EXIT, ...STATUS_CONFIG[PatientStatus.EXIT], icon: LogOut },
  ];

  return (
    <div
      className={`min-h-screen flex flex-col font-sans transition-all duration-500 ${isDarkMode ? 'bg-slate-950 text-slate-200' : 'bg-slate-50 text-slate-800'}`}
      style={{
        backgroundImage: isDarkMode
          ? `radial-gradient(circle at top right, ${branding.primaryColor}20, transparent 50%), radial-gradient(circle at bottom left, ${branding.secondaryColor}30, transparent 50%)`
          : `radial-gradient(circle at top right, ${branding.primaryColor}10, transparent 50%), radial-gradient(circle at bottom left, ${branding.secondaryColor}15, transparent 50%)`
      }}
    >

      {/* Navbar */}
      <div className={`px-5 md:px-8 py-4 shadow-md flex items-center justify-between sticky top-0 z-50 transition-all duration-500 backdrop-blur-2xl ${isDarkMode ? 'bg-slate-900/40 border-b border-white/5' : 'bg-white/70 border-b border-slate-200'}`}>
        <button onClick={() => navigate('/staff/dashboard')} className={`flex items-center text-sm font-bold transition-colors group ${isDarkMode ? 'text-white hover:text-white/70' : 'text-slate-600 hover:text-slate-900'}`}>
          <ChevronLeft className="w-5 h-5 mr-1 group-hover:-translate-x-1 transition-transform" />
          Volver
        </button>
        <div className="text-right flex items-center gap-2 md:gap-4">

          <div className="hidden md:block">
               <AdminNavbarUser primaryColor={branding?.primaryColor} theme={isDarkMode ? 'dark' : 'light'} />
          </div>

          <div className="flex items-center gap-1 md:gap-2 mr-2 md:mr-4">
            <button
              onClick={() => {
                const newVal = !isDarkMode;
                setIsDarkMode(newVal);
                const authData = storageService.getAuthData();
                if (authData) localStorage.setItem(`staff_dark_mode_${authData.hospitalId}`, String(newVal));
              }}
              className={`p-2 rounded-xl transition-all ${isDarkMode ? 'hover:bg-white/10 text-white/50' : 'hover:bg-black/5 text-slate-400'}`}
              title={isDarkMode ? 'Cambiar a Modo Claro' : 'Cambiar a Modo Oscuro'}
            >
              {isDarkMode ? <Sun className="w-5 h-5 hover:text-white" /> : <Moon className="w-5 h-5 hover:text-slate-800" />}
            </button>

            <div className={`flex flex-col items-center justify-center py-1.5 px-3 rounded-xl transition-all ${isDarkMode ? 'bg-white/5 border border-white/10' : 'bg-white border border-slate-200 shadow-sm'}`}>
              <button
                onClick={() => {
                  const newVal = !isSoundEnabled;
                  setIsSoundEnabled(newVal);
                  localStorage.setItem(`sound_enabled_${storageKeyId}`, String(newVal));
                  if (newVal) {
                    localStorage.setItem(`reminder_start_${storageKeyId}`, String(Date.now()));
                    playReminderChime(selectedSound);
                  }
                }}
                className={`group flex items-center justify-center transition-all gap-1.5`}
                title={isSoundEnabled ? 'Silenciar Recordatorio' : 'Activar Recordatorio (15 min)'}
              >
                {isSoundEnabled ? (
                  <>
                    <BellRing className="w-4 h-4 text-amber-400 group-hover:scale-110 transition-transform drop-shadow-[0_0_8px_rgba(251,191,36,0.6)]" />
                    <span className={`text-[10px] font-mono font-bold ${isDarkMode ? 'text-amber-400/80' : 'text-amber-600'}`}>{timeToNextReminder || '--:--'}</span>
                  </>
                ) : (
                  <>
                    <BellOff className={`w-4 h-4 ${isDarkMode ? 'text-white/30 group-hover:text-white/50' : 'text-slate-300 group-hover:text-slate-500'} transition-colors`} />
                    <span className={`text-[10px] uppercase font-black tracking-wider ${isDarkMode ? 'text-white/50 group-hover:text-white/80' : 'text-slate-500 group-hover:text-slate-700'}`}>Iniciar recordatorios</span>
                  </>
                )}
              </button>
            </div>

            {isSoundEnabled && (
              <select
                value={selectedSound}
                onChange={(e) => {
                  setSelectedSound(e.target.value);
                  localStorage.setItem(`sound_type_${storageKeyId}`, e.target.value);
                  playReminderChime(e.target.value);
                }}
                className={`ml-1 md:ml-2 text-[10px] font-bold py-1.5 px-2 rounded-xl outline-none transition-colors border shadow-sm ${isDarkMode ? 'bg-white/5 border-white/10 text-white/80' : 'bg-white border-slate-200 text-slate-700'}`}
              >
                <option value="campana">Campana</option>
                <option value="alerta">Alerta 1</option>
                <option value="clasico">Alerta 2</option>
                <option value="pedo">Especial 💨</option>
              </select>
            )}

          </div>

          <div className="flex flex-col items-end">
            <div className={`text-[9px] uppercase font-bold tracking-[0.2em] font-heading mb-0.5 ${isDarkMode ? 'text-white/50' : 'text-slate-400'}`}>Paciente</div>
            <div className={`text-lg md:text-xl font-black font-mono tracking-widest leading-none drop-shadow-md ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{patient.id}</div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-5 md:p-8 max-w-lg mx-auto w-full flex flex-col justify-start pt-8 space-y-6 pb-24 relative z-10">

        {(branding.logoDarkUrl || branding.logoUrl) && (
          <div className="flex justify-center mb-2">
            <div className="bg-white/10 backdrop-blur-md p-3 rounded-2xl border border-white/5 shadow-xl">
              <img src={branding.logoDarkUrl || branding.logoUrl} alt="Hospital Logo" className="h-12 md:h-16 object-contain drop-shadow-[0_0_15px_rgba(255,255,255,0.2)]" />
            </div>
          </div>
        )}

        <div className={`text-center mb-8 backdrop-blur-xl p-5 md:p-6 rounded-3xl border shadow-lg transition-all duration-500 ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-white/80 border-slate-200'}`}>
          <h2 className={`text-[10px] sm:text-xs uppercase tracking-[0.2em] font-black mb-3 font-heading ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>{roomId ? 'Control de Quirófano' : 'Control de Paciente'}</h2>
          <div className={`flex items-center justify-center text-sm md:text-base font-bold font-mono ${isDarkMode ? 'text-white/90' : 'text-slate-800'}`}>
            <Clock className={`w-4 h-4 mr-2 ${isDarkMode ? 'text-white/50' : 'text-slate-400'}`} />
            Inicio: {new Date(patient.admissionTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-6">
            {roomId && (
              <button
                onClick={() => setIsTransferModalOpen(true)}
                className={`flex items-center justify-center gap-2 px-6 py-4 md:py-3 shadow-lg text-[11px] md:text-xs font-black uppercase tracking-widest rounded-xl transition-all hover:-translate-y-0.5 active:scale-95 group backdrop-blur-md border ${isDarkMode
                  ? 'bg-white/10 hover:bg-white/20 text-white border-white/10'
                  : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-200'
                  }`}
              >
                <RefreshCw className="w-4 h-4 group-hover:rotate-180 transition-transform duration-500" style={{ color: branding.primaryColor || '#a78bfa' }} />
                Cambiar Quirófano
              </button>
            )}

            {patient.currentStatus !== PatientStatus.EXIT && (
              <button
                onClick={handleDoctorInfoToggle}
                disabled={isUpdating}
                className={`relative flex items-center justify-center gap-2 px-6 py-4 md:py-3 shadow-lg text-[11px] md:text-xs font-black uppercase tracking-widest rounded-xl transition-all hover:-translate-y-0.5 active:scale-95 group backdrop-blur-md border overflow-hidden ${patient.isDoctorEnCamino
                  ? 'bg-amber-400 text-amber-950 border-amber-400 hover:bg-amber-300 hover:border-amber-300'
                  : isDarkMode
                    ? 'bg-white/10 hover:bg-white/20 text-white border-white/10'
                    : 'bg-white hover:bg-slate-50 text-slate-700 border-slate-200'
                  } ${isUpdating ? 'opacity-50 cursor-not-allowed transform-none' : ''} ${!roomId ? 'md:col-span-2' : ''}`}
                style={patient.isDoctorEnCamino ? { boxShadow: '0 8px 25px -5px rgba(251, 191, 36, 0.5)' } : {}}
              >
                {!!patient.isDoctorEnCamino && <div className="absolute inset-0 rounded-xl ring-2 ring-amber-400/50 animate-pulse pointer-events-none"></div>}
                <Users className={`w-4 h-4 sm:w-5 sm:h-5 ${patient.isDoctorEnCamino ? 'animate-bounce' : ''}`} style={!patient.isDoctorEnCamino ? { color: branding.primaryColor || '#a78bfa' } : {}} />
                <span className="relative z-10 text-center flex-1 md:flex-none">
                  {patient.isDoctorEnCamino ? 'Aviso Enviado' : 'Médico Informará'}
                </span>
                {!!patient.isDoctorEnCamino && <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-amber-950" />}
              </button>
            )}
          </div>
        </div>

        {buttons.map((btn) => {
          const isActive = patient.currentStatus === btn.status;

          // Dynamic Style for Active Button
          const activeStyle = isActive ? {
            backgroundColor: branding.primaryColor,
            color: '#ffffff',
            borderColor: 'transparent',
            boxShadow: `0 15px 35px -5px ${branding.primaryColor}80`
          } : {};

          return (
            <div key={btn.status} className="flex flex-col space-y-3">
              <button
                onClick={() => handleStatusUpdate(btn.status)}
                disabled={isUpdating && !isActive}
                className={`
                  w-full py-5 md:py-6 rounded-[2rem] shadow-lg text-left px-6 md:px-8 transition-all transform duration-500
                  flex items-center justify-between group border relative overflow-hidden backdrop-blur-xl
                  ${!isActive ? (isDarkMode ? 'bg-white/5 border-white/5 hover:bg-white/10 hover:border-white/20 text-white/50 hover:text-white' : 'bg-white/60 border-slate-200 hover:bg-white hover:border-slate-300 text-slate-500 hover:text-slate-800') : 'scale-[1.02] ring-1 z-20'}
                  ${isUpdating && !isActive ? 'opacity-30 cursor-not-allowed' : ''}
                `}
                style={isActive ? { ...activeStyle, '--tw-ring-color': `${branding.primaryColor}80` } as any : {}}
              >
                {/* Subtle gradient overlay for active state */}
                {isActive && (
                  <div className="absolute inset-0 bg-gradient-to-tr from-white/20 to-transparent opacity-50 pointer-events-none"></div>
                )}

                {/* Active Glow behind icon */}
                {isActive && (
                  <div className="absolute -left-6 -top-6 w-32 h-32 bg-white/30 blur-3xl rounded-full pointer-events-none"></div>
                )}

                <div className="flex items-center gap-4 relative z-10">
                  <div className={`p-3 md:p-4 rounded-2xl md:rounded-3xl ${isActive
                    ? 'bg-white/20 text-white shadow-inner backdrop-blur-md'
                    : (isDarkMode ? 'bg-black/20 text-white/40 group-hover:bg-white/10 group-hover:text-white transition-colors border border-white/5' : 'bg-slate-100 text-slate-500 group-hover:bg-slate-200 group-hover:text-slate-800 transition-colors border border-slate-200')
                    }`}>
                    <btn.icon className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <span className={`text-base sm:text-lg font-bold tracking-wide font-heading uppercase ${isActive ? 'text-white drop-shadow-md' : (isDarkMode ? '' : 'text-slate-700 group-hover:text-slate-900')}`}>
                    {btn.label}
                  </span>
                </div>

                <div className="relative z-10">
                  {isActive ? (
                    <div className="bg-white/20 p-2 rounded-full shadow-inner border border-white/30">
                      {isUpdating ? <RefreshCw className="w-5 h-5 animate-spin text-white" /> : <CheckCircle2 className="w-5 h-5 text-white" />}
                    </div>
                  ) : (
                    <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity ${isDarkMode ? 'border-white/10' : 'border-slate-300'}`}>
                      <div className={`w-2.5 h-2.5 rounded-full ${isDarkMode ? 'bg-white/40' : 'bg-slate-400'}`}></div>
                    </div>
                  )}
                </div>
              </button>

              {/* Sub-Controls for Surgery Phase */}
              {isActive && btn.status === PatientStatus.SURGERY && (
                <div className={`backdrop-blur-xl rounded-3xl p-5 md:p-6 shadow-[0_0_30px_rgba(0,0,0,0.5)] border animate-fade-in space-y-6 relative mt-2 ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-white/90 border-slate-200'}`}>
                  <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-16 h-1 rounded-b-full ${isDarkMode ? 'bg-white/10' : 'bg-slate-300'}`}></div>

                  {/* Milestones */}
                  <div>
                    <h3 className={`text-[10px] font-black uppercase tracking-[0.2em] mb-4 pl-1 font-heading ${isDarkMode ? 'text-white/40' : 'text-slate-500'}`}>Hitos del Procedimiento</h3>
                    <div className="grid grid-cols-1 gap-3">
                      <button
                        onClick={() => handleStatusUpdate(PatientStatus.SURGERY, SurgerySubStatus.INCISION)}
                        className={`p-4 md:p-5 rounded-2xl flex items-center gap-4 transition-all border ${patient.currentSubStatus === SurgerySubStatus.INCISION
                          ? (isDarkMode ? 'bg-white/10 border-white/20 shadow-lg' : 'bg-slate-50 border-slate-300 shadow-md')
                          : (isDarkMode ? 'bg-black/20 border-white/5 text-white/50 hover:bg-white/5 hover:text-white' : 'bg-slate-100 border-slate-200 text-slate-500 hover:bg-white hover:text-slate-800')
                          }`}
                        style={patient.currentSubStatus === SurgerySubStatus.INCISION ? {
                          borderColor: branding.primaryColor,
                          boxShadow: `0 0 20px ${branding.primaryColor}20`,
                          color: isDarkMode ? '#fff' : branding.secondaryColor
                        } : {}}
                      >
                        <Scissors className={`w-5 h-5 md:w-6 md:h-6`} style={{ color: patient.currentSubStatus === SurgerySubStatus.INCISION ? branding.primaryColor || '#a78bfa' : undefined }} />
                        <span className="font-bold tracking-wide">1. Incisión / Inicio</span>
                      </button>

                      {/* PROCEDURE PHASE */}
                      <div className={`rounded-2xl border transition-all overflow-hidden ${patient.currentSubStatus === SurgerySubStatus.PROCEDURE
                        ? (isDarkMode ? 'bg-white/10 border-white/20 shadow-2xl backdrop-blur-2xl' : 'bg-slate-50 border-slate-300 shadow-xl backdrop-blur-2xl')
                        : (isDarkMode ? 'bg-black/20 border-white/5' : 'bg-slate-100 border-slate-200')
                        }`}
                        style={patient.currentSubStatus === SurgerySubStatus.PROCEDURE ? { borderColor: branding.primaryColor, boxShadow: `0 0 30px ${branding.primaryColor}20` } : {}}
                      >
                        <button
                          onClick={() => handleStatusUpdate(PatientStatus.SURGERY, SurgerySubStatus.PROCEDURE)}
                          className={`w-full p-4 md:p-5 flex items-center gap-4 text-left ${patient.currentSubStatus === SurgerySubStatus.PROCEDURE
                            ? (isDarkMode ? 'text-white' : 'text-slate-900')
                            : (isDarkMode ? 'text-white/50 hover:bg-white/5 hover:text-white' : 'text-slate-500 hover:bg-white hover:text-slate-800')
                            }`}
                        >
                          <Activity className={`w-5 h-5 md:w-6 md:h-6`} style={{ color: patient.currentSubStatus === SurgerySubStatus.PROCEDURE ? branding.primaryColor || '#a78bfa' : undefined }} />
                          <span className="font-bold tracking-wide">2. Procedimiento Principal</span>
                        </button>

                        {/* PULSE BUTTON & GLOW BAR */}
                        {patient.currentSubStatus === SurgerySubStatus.PROCEDURE && (
                          <div className="px-5 pb-6 animate-fade-in relative z-10">
                            <div className={`flex items-center justify-between text-[10px] font-black uppercase tracking-widest mb-3 font-heading ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>
                              <span>
                                {patient.currentCondition === SurgeryCondition.DELAYED ? (
                                  <span className="text-orange-400 flex items-center gap-1.5 opacity-100"><Clock className="w-3.5 h-3.5" /> Demorado: {patient.surgeryProgress}%</span>
                                ) : (
                                  `Progreso: ${patient.surgeryProgress}%`
                                )}
                              </span>
                              <span>Meta: 90%</span>
                            </div>

                            {/* Glowing Progress Bar */}
                            <div className="w-full bg-black/40 rounded-full h-2.5 mb-6 border border-white/10 overflow-hidden relative shadow-inner">
                              <div
                                className="h-full rounded-full transition-all duration-700 ease-out relative"
                                style={{ width: `${patient.surgeryProgress}%`, backgroundColor: branding.primaryColor || '#a78bfa', boxShadow: `0 0 10px ${branding.primaryColor}` }}
                              >
                                <div className="absolute inset-0 bg-white/30 w-full h-full animate-[shimmer_2s_infinite]"></div>
                              </div>
                            </div>

                            <button
                              onClick={(e) => { e.stopPropagation(); handleProgressIncrement(); }}
                              disabled={patient.surgeryProgress >= 90 || isUpdating}
                              className="w-full text-white font-black py-4 rounded-xl flex items-center justify-center gap-2 shadow-lg active:scale-95 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:brightness-110 border border-white/20 uppercase tracking-widest text-xs"
                              style={{ backgroundColor: branding.primaryColor || '#a78bfa', boxShadow: `0 8px 25px -5px ${branding.primaryColor}60` }}
                            >
                              <RefreshCw className={`w-4 h-4 ${isUpdating ? 'animate-spin' : ''}`} />
                              {isUpdating ? 'Guardando...' : 'Reportar Avance (+5%)'}
                            </button>
                            <p className={`text-[10px] text-center tracking-widest uppercase mt-4 font-bold ${isDarkMode ? 'text-white/30' : 'text-slate-400'}`}>
                              Toque para actualizar monitor
                            </p>
                          </div>
                        )}
                      </div>

                      <button
                        onClick={() => handleStatusUpdate(PatientStatus.SURGERY, SurgerySubStatus.CLOSING)}
                        className={`p-4 md:p-5 rounded-2xl flex items-center gap-4 transition-all border ${patient.currentSubStatus === SurgerySubStatus.CLOSING
                          ? (isDarkMode ? 'bg-white/10 border-white/20 shadow-lg' : 'bg-slate-50 border-slate-300 shadow-xl')
                          : (isDarkMode ? 'bg-black/20 border-white/5 text-white/50 hover:bg-white/5 hover:text-white' : 'bg-slate-100 border-slate-200 text-slate-500 hover:bg-white hover:text-slate-800')
                          }`}
                        style={patient.currentSubStatus === SurgerySubStatus.CLOSING ? {
                          borderColor: branding.primaryColor,
                          boxShadow: `0 0 20px ${branding.primaryColor}20`,
                          color: isDarkMode ? '#fff' : branding.secondaryColor
                        } : {}}
                      >
                        <FileText className={`w-5 h-5 md:w-6 md:h-6`} style={{ color: patient.currentSubStatus === SurgerySubStatus.CLOSING ? branding.primaryColor || '#a78bfa' : undefined }} />
                        <span className="font-bold tracking-wide">3. Cierre / Sutura</span>
                      </button>

                      <button
                        onClick={() => handleStatusUpdate(PatientStatus.SURGERY, SurgerySubStatus.FINISHED)}
                        className={`p-4 md:p-5 rounded-2xl flex items-center gap-4 transition-all border ${patient.currentSubStatus === SurgerySubStatus.FINISHED
                          ? (isDarkMode ? 'bg-white/10 border-white/20 shadow-lg' : 'bg-slate-50 border-slate-300 shadow-xl')
                          : (isDarkMode ? 'bg-black/20 border-white/5 text-white/50 hover:bg-white/5 hover:text-white' : 'bg-slate-100 border-slate-200 text-slate-500 hover:bg-white hover:text-slate-800')
                          }`}
                        style={patient.currentSubStatus === SurgerySubStatus.FINISHED ? {
                          borderColor: branding.primaryColor,
                          boxShadow: `0 0 20px ${branding.primaryColor}20`,
                          color: isDarkMode ? '#fff' : branding.secondaryColor
                        } : {}}
                      >
                        <CheckCircle2 className={`w-5 h-5 md:w-6 md:h-6`} style={{ color: patient.currentSubStatus === SurgerySubStatus.FINISHED ? branding.primaryColor || '#a78bfa' : undefined }} />
                        <span className="font-bold tracking-wide">4. Procedimiento finalizado</span>
                      </button>
                    </div>
                  </div>

                  {/* Traffic Light */}
                  <div className="pt-2">
                    <h3 className={`text-[10px] font-black uppercase tracking-[0.2em] mb-4 pl-1 font-heading ${isDarkMode ? 'text-white/40' : 'text-slate-500'}`}>Estado de Riesgo</h3>
                    <div className="grid grid-cols-3 gap-3">
                      <button
                        onClick={() => handleStatusUpdate(PatientStatus.SURGERY, patient.currentSubStatus, SurgeryCondition.NORMAL)}
                        className={`flex flex-col items-center justify-center p-3 md:p-4 rounded-2xl border transition-all ${patient.currentCondition === SurgeryCondition.NORMAL ? 'bg-emerald-500/10 border-emerald-500/30 shadow-[0_0_15px_rgba(16,185,129,0.2)]' : (isDarkMode ? 'bg-black/20 border-white/5 opacity-50 hover:opacity-80' : 'bg-slate-100 border-slate-200 opacity-60 hover:opacity-100')}`}
                      >
                        <div className={`w-3 h-3 rounded-full bg-emerald-400 mb-2 ${patient.currentCondition === SurgeryCondition.NORMAL ? 'shadow-[0_0_10px_rgba(52,211,153,0.8)] animate-pulse' : ''}`}></div>
                        <span className={`text-[9px] md:text-[10px] font-black uppercase tracking-widest ${isDarkMode ? 'text-white' : 'text-slate-700'}`}>Normal</span>
                      </button>

                      <button
                        onClick={() => handleStatusUpdate(PatientStatus.SURGERY, patient.currentSubStatus, SurgeryCondition.DELAYED)}
                        className={`flex flex-col items-center justify-center p-3 md:p-4 rounded-2xl border transition-all ${patient.currentCondition === SurgeryCondition.DELAYED ? 'bg-amber-500/10 border-amber-500/30 shadow-[0_0_15px_rgba(245,158,11,0.2)]' : (isDarkMode ? 'bg-black/20 border-white/5 opacity-50 hover:opacity-80' : 'bg-slate-100 border-slate-200 opacity-60 hover:opacity-100')}`}
                      >
                        <div className={`w-3 h-3 rounded-full bg-amber-400 mb-2 ${patient.currentCondition === SurgeryCondition.DELAYED ? 'shadow-[0_0_10px_rgba(251,191,36,0.8)] animate-pulse' : ''}`}></div>
                        <span className={`text-[9px] md:text-[10px] font-black uppercase tracking-widest ${isDarkMode ? 'text-white' : 'text-slate-700'}`}>Retraso</span>
                      </button>

                      <button
                        onClick={() => handleStatusUpdate(PatientStatus.SURGERY, patient.currentSubStatus, SurgeryCondition.EXTENDED)}
                        className={`flex flex-col items-center justify-center p-3 md:p-4 rounded-2xl border transition-all ${patient.currentCondition === SurgeryCondition.EXTENDED ? 'bg-rose-500/10 border-rose-500/30 shadow-[0_0_15px_rgba(225,29,72,0.2)]' : (isDarkMode ? 'bg-black/20 border-white/5 opacity-50 hover:opacity-80' : 'bg-slate-100 border-slate-200 opacity-60 hover:opacity-100')}`}
                      >
                        <div className={`w-3 h-3 rounded-full bg-rose-500 mb-2 ${patient.currentCondition === SurgeryCondition.EXTENDED ? 'shadow-[0_0_10px_rgba(244,63,94,0.8)] animate-pulse' : ''}`}></div>
                        <span className={`text-[9px] md:text-[10px] font-black uppercase tracking-widest ${isDarkMode ? 'text-white' : 'text-slate-700'}`}>Extendido</span>
                      </button>
                    </div>
                  </div>

                </div>
              )}
            </div>
          );
        })}


        {/* Discrete Bug Report Footer */}
        <div className={`text-center py-6 mt-8 border-t ${isDarkMode ? 'border-white/5' : 'border-slate-200/50'}`}>
          <BugReportWidget isDarkMode={isDarkMode} hospitalName={hospital?.name} />
        </div>
      </div>

      {/* Transfer Modal */}
      {isTransferModalOpen && (
        <div className={`fixed inset-0 z-50 flex items-center justify-center p-6 backdrop-blur-md animate-fade-in ${isDarkMode ? 'bg-slate-950/80' : 'bg-slate-900/40'}`}>
          <div className={`w-full max-w-sm rounded-[2rem] shadow-2xl overflow-hidden border animate-scale-in ${isDarkMode ? 'bg-slate-900 border-white/10' : 'bg-white border-slate-200'}`}>
            <div className={`p-6 border-b flex justify-between items-center ${isDarkMode ? 'border-white/5' : 'border-slate-100'}`}>
              <h3 className={`text-lg font-bold font-heading tracking-wide ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>Cambiar Quirófano</h3>
              <button onClick={() => setIsTransferModalOpen(false)} className={`p-2 rounded-full transition-colors group ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}>
                <X className={`w-5 h-5 ${isDarkMode ? 'text-white/50 group-hover:text-white' : 'text-slate-400 group-hover:text-slate-800'}`} />
              </button>
            </div>
            <div className="p-8">
              <p className={`text-[11px] mb-8 text-center px-2 leading-relaxed tracking-wider ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>Seleccione el nuevo quirófano para trasladar al paciente <span className={`font-mono font-black ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{patient.id}</span>.</p>

              <div className="grid grid-cols-2 gap-4 mb-8">
                {rooms.map(room => {
                  const isCurrent = room.id === roomId;
                  const isOccupied = !!room.currentPatientId && !isCurrent;

                  return (
                    <button
                      key={room.id}
                      disabled={isOccupied || isCurrent || isUpdating}
                      onClick={() => handleTransfer(room.id)}
                      className={`
                        p-5 rounded-[1.5rem] border transition-all flex flex-col items-center gap-3 relative overflow-hidden backdrop-blur-md
                        ${isCurrent ? (isDarkMode ? 'bg-white/10 border-white/20 text-white cursor-default ring-1 ring-white/30 shadow-lg' : 'bg-slate-50 border-slate-300 text-slate-800 cursor-default ring-1 ring-slate-400/30 shadow-md') : ''}
                        ${isOccupied ? (isDarkMode ? 'opacity-30 cursor-not-allowed bg-black/40 border-white/5 text-white/50' : 'opacity-40 cursor-not-allowed bg-slate-100 border-slate-200 text-slate-500') : ''}
                        ${!isCurrent && !isOccupied ? (isDarkMode ? 'hover:border-white/30 hover:bg-white/5 hover:shadow-2xl hover:-translate-y-1 bg-black/20 text-white/80 hover:text-white font-black shadow-sm border-white/10' : 'hover:border-slate-300 hover:bg-slate-50 hover:shadow-xl hover:-translate-y-1 bg-white text-slate-600 hover:text-slate-900 font-black shadow-sm border-slate-200') : ''}
                      `}
                      style={isCurrent ? { borderColor: branding.primaryColor, boxShadow: `0 0 15px ${branding.primaryColor}40` } : {}}
                    >
                      {isCurrent && <div className="absolute top-0 right-0 p-1.5 text-white rounded-bl-xl shadow-sm" style={{ backgroundColor: branding.primaryColor || '#a78bfa' }}><CheckCircle2 className="w-3 h-3" /></div>}
                      <Bed className={`w-8 h-8 ${isCurrent ? (isDarkMode ? 'text-white' : 'text-slate-800') : (isDarkMode ? 'text-white/30' : 'text-slate-300')}`} style={isCurrent ? { color: branding.primaryColor || '#a78bfa' } : {}} />
                      <span className="text-[10px] font-black leading-tight uppercase tracking-widest">{room.name}</span>
                      {isOccupied && <span className="absolute bottom-1.5 text-[7px] font-black uppercase tracking-widest text-rose-400 font-heading">Ocupado</span>}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setIsTransferModalOpen(false)}
                className={`w-full py-2 text-[10px] font-black uppercase tracking-[0.2em] transition-colors ${isDarkMode ? 'text-white/30 hover:text-rose-400' : 'text-slate-400 hover:text-rose-500'}`}
              >
                Cancelar Traslado
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffRoomControl;